import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { Box, Button, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { RiCalendarScheduleLine, RiMoneyDollarCircleLine, RiWalletLine } from "@remixicon/react";
import { getTutorCycleHours, getTutorPayouts } from "../../../services/dataService";
import { openReceipt } from "../../../services/payouts";
import { hasPermission, permissions } from "../../../config/permissions";
import { cycleDetailRows, cycleSubtitle, payoutRow, payRows, sortPayouts } from "../../../utils/tutorDetail";
import { periodRange } from "../../../utils/payCycle";
import { sessionsLabel } from "../../../utils/studentPresentation";
import { allShowingLabel } from "../../../utils/kitPresentation";
import { SectionCard, TableCard } from "../../../components/kit/SectionCard";
import DetailRows from "../../../components/kit/DetailRows";
import Pager from "../../../components/kit/Pager";
import StatStrip from "../../../components/kit/StatStrip";
import StatusChip from "../../../components/kit/StatusChip";
import { ErrorState } from "../../../components/kit/States";
import { TableMessageRow, TwoLineCell } from "../../../components/kit/TableParts";
import { errorText } from "../../Students/tabs/TabState";

const COLUMNS = ["Date", "Cycle", "Amount", "Receipt", "Owed before", "Owed after", "Notes"];

// What the tutor earns and was paid (047 D44, D52): this cycle's figures, the last cycle's
// lessons (the old drawer's Last Cycle Details), the session balance, and every payout. Open
// Payouts opens the Payouts page, where staff pick a cycle and pay it (053 R8); Adjust balance
// opens the page's dialog.
const PayTab = () => {
  const { tutor, userRole, openDialog, notify } = useOutletContext();
  const uid = tutor.id;
  const [hours, setHours] = useState(null);
  const [hoursError, setHoursError] = useState("");
  const [payouts, setPayouts] = useState(null);
  const [payoutsError, setPayoutsError] = useState("");
  const canPay = hasPermission(userRole, permissions.EDIT_FINANCIAL);
  const canAdjust = hasPermission(userRole, permissions.ADJUST_WALLET);

  const loadHours = useCallback(async () => {
    setHoursError("");
    try {
      setHours(await getTutorCycleHours(uid));
    } catch (e) {
      console.error("Error loading the cycle hours:", e);
      setHoursError(errorText(e, "Failed to load the cycle hours."));
    }
  }, [uid]);

  const loadPayouts = useCallback(async () => {
    setPayouts(null);
    setPayoutsError("");
    try {
      setPayouts(sortPayouts(await getTutorPayouts(uid)));
    } catch (e) {
      console.error("Error loading the payouts:", e);
      setPayoutsError(errorText(e, "Failed to load the payouts."));
    }
  }, [uid]);

  useEffect(() => {
    loadHours();
    loadPayouts();
  }, [loadHours, loadPayouts]);

  const payoutsLoading = payouts === null && !payoutsError;

  return (
    <Box
      sx={{
        display: "grid",
        gap: "16px",
        alignItems: "start",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
      }}
    >
      <SectionCard title="This pay cycle" Icon={RiMoneyDollarCircleLine} subtitle={cycleSubtitle(hours?.cycles)}>
        {hoursError && <ErrorState compact title={hoursError} onRetry={loadHours} />}
        <DetailRows rows={payRows({ tutor, hours, payouts })} />
        {canPay && (
          <Button size="large" variant="contained" component={RouterLink} to="/payouts">
            Open Payouts
          </Button>
        )}
      </SectionCard>

      <SectionCard
        title="Last pay cycle"
        Icon={RiCalendarScheduleLine}
        subtitle={hours ? `Pay cycle ${periodRange(hours.cycles.previous)}` : ""}
      >
        <DetailRows rows={cycleDetailRows(hours?.details)} />
      </SectionCard>

      <SectionCard title="Session balance" Icon={RiWalletLine}>
        <StatStrip items={[{ key: "balance", label: "Current balance", value: sessionsLabel(tutor.WalletBalance) }]} />
        {canAdjust && (
          <Button size="large" variant="outlined" onClick={() => openDialog("adjust")}>
            Adjust balance
          </Button>
        )}
      </SectionCard>

      <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
        <TableCard
          title="Payout history"
          subtitle="Newest first"
          footer={<Pager label={payoutsLoading || payoutsError ? "" : allShowingLabel(payouts.length)} />}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutsLoading || payoutsError || payouts.length === 0 ? (
                  <TableMessageRow
                    colSpan={COLUMNS.length}
                    loading={payoutsLoading}
                    error={payoutsError}
                    onRetry={loadPayouts}
                    emptyText="No payouts recorded yet."
                  />
                ) : (
                  payouts.map((p) => {
                    const row = payoutRow(p);
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <TwoLineCell top={row.when ? row.when.date : "-"} bottom={row.when?.time} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{row.cycle}</TableCell>
                        <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                          {row.amount}
                          {row.undone && <StatusChip label="Undone" />}
                        </TableCell>
                        <TableCell>
                          {row.hasReceipt ? (
                            <Link component="button" type="button" onClick={() => openReceipt(p.receiptPath).catch((e) => notify(e.message, "error"))}>
                              {row.receipt}
                            </Link>
                          ) : (
                            row.receipt
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{row.before}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{row.after}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <TwoLineCell wrap top={row.notes} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      </Box>
    </Box>
  );
};

export default PayTab;
